/**
 * Google Drive Import — Shortcut Strategy
 *
 * Strategy: Original file stays in user's folder, Bigwa gets a shortcut.
 *
 * Flow:
 * 1. Create shortcut in Bigwa/Biblioteca/PDFs/ pointing to the original
 * 2. Make original shareable (for webViewLink)
 * 3. Return original file ID + shortcut ID
 *
 * On deletion:
 * 1. Delete shortcut from Bigwa
 * 2. Delete reference from library
 * 3. Original PDF is NEVER moved, copied, or touched
 *
 * Legacy support: returnPdfToOriginalFolder kept for old "Replicate Invertido" imports.
 */

import { googleAuth } from '../googleAuth';
import { logger } from '../../utils/logger';
import { DRIVE_API_BASE } from './core';
import { driveFetch } from './driveFetch';
import {
  NetworkError,
  NotFoundError,
  ForbiddenError,
  UnknownError,
} from '../../core/errors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ImportResult {
  fileId: string;
  driveFileUrl: string;
  driveFileName: string;
  originalParentFolderId: string;
  originalFileName: string;
  driveShortcutId?: string;
  wasCopied: boolean; // true if fallback copy was used (shared file)
}

export interface ImportBatchResult {
  imported: ImportResult[];
  duplicates: string[];
  errors: Array<{ fileName: string; error: string }>;
}

interface DriveFileMetadata {
  id: string;
  name: string;
  mimeType: string;
  parents?: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_PARALLEL = 5;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getToken(): Promise<string> {
  const token = await googleAuth.ensureFreshToken();
  if (!token) {
    throw new NetworkError('Sem token de autenticação');
  }
  return token.trim();
}

async function driveGet<T>(path: string, token: string): Promise<T> {
  const response = await driveFetch(`${DRIVE_API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 404) {
    throw new NotFoundError('Arquivo não encontrado no Google Drive');
  }
  if (response.status === 403) {
    throw new ForbiddenError('Sem permissão para acessar este arquivo');
  }
  if (!response.ok) {
    throw new NetworkError(`Erro na API do Drive: ${response.status}`);
  }

  return response.json();
}

async function drivePatch<T>(
  path: string,
  token: string,
  body: Record<string, unknown>,
  queryParams?: string,
): Promise<T> {
  const url = queryParams
    ? `${DRIVE_API_BASE}${path}?${queryParams}`
    : `${DRIVE_API_BASE}${path}`;

  const response = await driveFetch(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (response.status === 404) {
    throw new NotFoundError('Arquivo não encontrado');
  }
  if (response.status === 403) {
    throw new ForbiddenError('Sem permissão para modificar este arquivo');
  }
  if (!response.ok) {
    throw new NetworkError(`Erro ao modificar arquivo: ${response.status}`);
  }

  return response.json();
}

async function drivePost<T>(
  path: string,
  token: string,
  body: Record<string, unknown>,
): Promise<T> {
  const response = await driveFetch(`${DRIVE_API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new NetworkError(`Erro na API do Drive: ${response.status}`);
  }

  return response.json();
}

/**
 * Get shareable link for a file (webViewLink).
 */
async function getShareableLink(fileId: string, token: string): Promise<string> {
  const data = await driveGet<{ webViewLink?: string; webContentLink?: string }>(
    `/files/${fileId}?fields=webViewLink,webContentLink`,
    token,
  );
  return data.webViewLink || data.webContentLink || '';
}

/**
 * Make file shareable (anyone with link can view).
 */
async function makeShareable(fileId: string, token: string): Promise<void> {
  try {
    await driveFetch(`${DRIVE_API_BASE}/files/${fileId}/permissions`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role: 'reader', type: 'anyone' }),
    });
  } catch {
    logger.warn('DriveImport', `Falha ao tornar arquivo compartilhável: ${fileId}`);
  }
}

/**
 * Delete a shortcut permanently from Google Drive.
 * Used when removing an imported reference — the original file is untouched.
 */
export async function deleteImportShortcut(shortcutId: string): Promise<void> {
  const token = await getToken();

  try {
    const response = await driveFetch(`${DRIVE_API_BASE}/files/${shortcutId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok && response.status !== 404) {
      logger.warn('DriveImport', `Falha ao deletar shortcut: ${response.status}`);
    }
  } catch {
    // Shortcut may have been manually deleted — not critical
    logger.warn('DriveImport', 'Shortcut já removido ou inacessível');
  }
}

/**
 * Run promises with concurrency limit.
 */
async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  limit: number,
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = [];
  let index = 0;

  async function runNext(): Promise<void> {
    while (index < tasks.length) {
      const currentIndex = index++;
      try {
        const value = await tasks[currentIndex]();
        results[currentIndex] = { status: 'fulfilled', value };
      } catch (reason) {
        results[currentIndex] = { status: 'rejected', reason };
      }
    }
  }

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => runNext());
  await Promise.all(workers);
  return results;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Import a single file from Drive using the "Shortcut" strategy.
 *
 * The original file NEVER moves or gets copied. Instead:
 * 1. Verify file exists
 * 2. Create shortcut in Bigwa/Biblioteca/PDFs/ pointing to original
 * 3. Make original shareable (for webViewLink)
 * 4. Return original file ID + shortcut ID
 *
 * This eliminates all duplication bugs from move/copy operations.
 */
export async function importFromDriveWithReplicate(params: {
  fileId: string;
  originalParentFolderId: string;
  originalFileName: string;
  targetFolderId: string;
  newFileName: string;
}): Promise<ImportResult> {
  const { fileId, originalParentFolderId, originalFileName, targetFolderId, newFileName } = params;
  const token = await getToken();

  // STEP 1: Verify file exists and get current metadata
  const metadata = await driveGet<DriveFileMetadata>(
    `/files/${fileId}?fields=id,name,mimeType,parents`,
    token,
  );

  // Determine actual parent (Picker may not always send correct parentId)
  const actualParentId = originalParentFolderId || metadata.parents?.[0] || '';

  // STEP 2: Create shortcut in Bigwa/Biblioteca/PDFs/ pointing to original
  let driveShortcutId: string | undefined;
  try {
    const shortcut = await drivePost<{ id: string }>('/files', token, {
      name: newFileName,
      mimeType: 'application/vnd.google-apps.shortcut',
      shortcutDetails: { targetId: fileId },
      parents: [targetFolderId],
    });
    driveShortcutId = shortcut.id;
  } catch (shortcutError) {
    logger.error('DriveImport', 'Falha ao criar shortcut no Bigwa', shortcutError);
    throw new NetworkError('Não foi possível importar o arquivo. Tente novamente.');
  }

  // STEP 3: Make original file shareable (for webViewLink)
  await makeShareable(fileId, token);

  // STEP 4: Get shareable link from original
  const driveFileUrl = await getShareableLink(fileId, token);

  return {
    fileId,              // Original file ID — never changes
    driveFileUrl,
    driveFileName: newFileName,
    originalParentFolderId: actualParentId,
    originalFileName,
    driveShortcutId,     // Shortcut lives in Bigwa folder
    wasCopied: false,    // No copy ever happens
  };
}

/**
 * Check if a file already exists in the library by its Drive file ID.
 * Ignores soft-deleted works (isDeleted) so re-import after trash is allowed.
 */
export function isDuplicateByFileId(
  fileId: string,
  existingWorks: Array<{ driveFileId?: string; isDeleted?: boolean }>,
): boolean {
  return existingWorks.some((w) => w.driveFileId === fileId && !w.isDeleted);
}

/**
 * Import multiple files from Drive with concurrency limit.
 *
 * Returns a summary with imported files, duplicates, and errors.
 * Each file is processed independently — one failure doesn't block others.
 */
export async function importBatchFromDrive(params: {
  files: Array<{
    fileId: string;
    originalParentFolderId: string;
    originalFileName: string;
    newFileName: string;
  }>;
  targetFolderId: string;
  existingWorks: Array<{ driveFileId?: string; isDeleted?: boolean }>;
  onProgress?: (fileId: string, status: 'importing' | 'success' | 'duplicate' | 'error', message?: string) => void;
}): Promise<ImportBatchResult> {
  const { files, targetFolderId, existingWorks, onProgress } = params;
  const result: ImportBatchResult = {
    imported: [],
    duplicates: [],
    errors: [],
  };

  const tasks = files.map((file) => async () => {
    // Check duplicate before importing
    if (isDuplicateByFileId(file.fileId, existingWorks)) {
      onProgress?.(file.fileId, 'duplicate', 'Este PDF já está na sua Biblioteca');
      result.duplicates.push(file.originalFileName);
      return;
    }

    onProgress?.(file.fileId, 'importing', 'Movendo para o Bigwa...');

    try {
      const importResult = await importFromDriveWithReplicate({
        fileId: file.fileId,
        originalParentFolderId: file.originalParentFolderId,
        originalFileName: file.originalFileName,
        targetFolderId,
        newFileName: file.newFileName,
      });
      result.imported.push(importResult);
      onProgress?.(file.fileId, 'success', importResult.wasCopied ? 'Copiado para o Bigwa' : 'Importado');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro desconhecido';
      result.errors.push({ fileName: file.originalFileName, error: message });
      onProgress?.(file.fileId, 'error', message);
      logger.error('DriveImport', `Falha ao importar ${file.originalFileName}`, error);
    }
  });

  await runWithConcurrency(tasks, MAX_PARALLEL);
  return result;
}

/**
 * Return a PDF to its original folder (used when deleting a reference).
 *
 * Steps:
 * 1. Verify original folder exists (fallback to Drive root if not)
 * 2. Delete the shortcut
 * 3. Move PDF back to original folder (or root)
 *
 * Returns the folder name where the file was moved to.
 */
export async function returnPdfToOriginalFolder(params: {
  fileId: string;
  originalParentFolderId: string;
  bigwaFolderId: string;
  driveShortcutId?: string;
}): Promise<{ movedToRoot: boolean; folderName: string }> {
  const { fileId, originalParentFolderId, bigwaFolderId, driveShortcutId } = params;
  const token = await getToken();

  // STEP 1: Verify original folder still exists, fallback to root
  let targetFolderId = originalParentFolderId;
  let movedToRoot = false;
  let folderName = '';

  try {
    const data = await driveGet<{ id: string; name: string }>(
      `/files/${originalParentFolderId}?fields=id,name`,
      token,
    );
    folderName = data.name || 'Pasta original';
  } catch (error) {
    if (error instanceof NotFoundError) {
      logger.info('DriveImport', 'Pasta original não existe mais, devolvendo para raiz do Drive');
      targetFolderId = 'root';
      movedToRoot = true;
      folderName = 'Meu Drive';
    } else {
      throw error;
    }
  }

  // STEP 2: Delete shortcut (if it exists)
  if (driveShortcutId) {
    try {
      const response = await driveFetch(`${DRIVE_API_BASE}/files/${driveShortcutId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok && response.status !== 404) {
        logger.warn('DriveImport', `Falha ao deletar shortcut: ${response.status}`);
      }
    } catch {
      // Shortcut may have been deleted by user — not critical
      logger.warn('DriveImport', 'Shortcut já removido ou inacessível');
    }
  }

  // STEP 3: Move PDF back to target folder
  try {
    const queryParams = new URLSearchParams({
      addParents: targetFolderId,
      removeParents: bigwaFolderId,
    }).toString();

    await drivePatch<{ id: string }>(
      `/files/${fileId}`,
      token,
      {},
      queryParams,
    );
  } catch (error) {
    throw new UnknownError(
      'Não foi possível devolver o PDF. Ele permanece na pasta Bigwa.',
      { cause: error instanceof Error ? error : undefined },
    );
  }

  return { movedToRoot, folderName };
}

/**
 * Trash a file in Google Drive (move to trash, not permanent delete).
 */
export async function trashDriveFile(fileId: string): Promise<void> {
  const token = await getToken();

  try {
    await drivePatch<{ id: string }>(
      `/files/${fileId}`,
      token,
      { trashed: true },
    );
  } catch (error) {
    logger.error('DriveImport', `Falha ao enviar arquivo para lixeira: ${fileId}`, error);
    throw error;
  }
}

/**
 * Clean a filename by removing common artifacts.
 * Used as fallback when metadata is not yet extracted.
 */
export function cleanOriginalFilename(name: string): string {
  return name
    // Remove extension
    .replace(/\.pdf$/i, '')
    // Remove parenthesized blocks containing internal params (hash-page=N, id=..., etc.)
    .replace(/\s*\([^)]*(?:page=|id=|#)[^)]*\)/gi, '')
    // Remove trailing hash-page fragments without parens (e.g. "...1C008ED68-page=0")
    .replace(/[\s-]*[A-Fa-f0-9]{7,}-page=\d+\)?/gi, '')
    // Remove version markers: v2, v3, _v2, (v2), _final, _FINAL
    .replace(/[_\s-]*(v\d+|final|FINAL|draft|DRAFT|copy|Copy|copia|Copia)/gi, '')
    // Remove duplicate markers: (1), (2), _1, _2
    .replace(/[_\s]*\(\d+\)/g, '')
    .replace(/[_\s]+\d+$/, '')
    // Remove leading/trailing underscores, hyphens, spaces
    .replace(/^[_\s-]+|[_\s-]+$/g, '')
    // Collapse multiple spaces/underscores
    .replace(/[_\s]{2,}/g, ' ')
    .trim()
    + '.pdf';
}
